import React from 'react'
import PropTypes from 'prop-types'

import classnames from 'classnames'
import StringUtils from '@leverege/string-util'
import { RelationshipCreator } from '@leverege/api-attributes'
import { TextInput, Flex, Button, Text, Dialog } from '@leverege/ui-elements'

import { ListGroup, ListGroupItem } from 'react-bootstrap'
import style from './AddRelationshipDialog.css'

export default class AddRelationshipDialog extends React.Component {
  
  static contextTypes = {
    api : PropTypes.object.isRequired
  }

  static propTypes = {
    onAddRelationship : PropTypes.func.isRequired,
    projectId : PropTypes.string.isRequired,
    path : PropTypes.string.isRequired
  }

  constructor( props ) {
    super( props )
    this.state = { 
      blueprints : null, 
      blueprintId : null, 
      childPath : this.getDefaultChildPath( props?.blueprint?.data?.alias || props?.blueprint?.data?.name, props?.path ) }
  }

  getDefaultChildPath( alias, path ) {
    if ( typeof alias === 'string' ) {
      const clean = alias.split( ' ' )
      return clean.map( ( v, index ) => {
        if ( index === 0 ) {
          return `${v.slice( 0, 1 ).toLowerCase()}${v.slice( 1 )}`
        }
        return v
      } ).join( '' )
    }
    return path
  }

  onItemSelected( e ) {
    this.setState( { blueprintId : e.id, targetName : e.name } )
  }

  onCreate( type ) {
    const { blueprintId, childPath } = this.state
    const { path, onAddRelationship } = this.props
    const r = RelationshipCreator.create( path, path )
    r.relationshipType = type
    r.targetBlueprintId = blueprintId
    r.childPath = childPath
    
    if ( onAddRelationship ) {
      onAddRelationship( r )
    }
    this.setState( { blueprintId : null, targetName : null } )
  }

  componentDidMount( ) {
    
    const { api } = this.context
    const { projectId } = this.props
    this.mounted = true
    if ( api && projectId ) {
      api.getBlueprints( projectId ).then( ( result ) => {
        if ( this.mounted ) {
          this.setState( { blueprints : result.items, loading : false } )
        }
      } ).catch( ( ex ) => {
        if ( this.mounted ) {
          this.setState( { loading : true } )
        }
      } )
    }
  }

  componentWillUnmount() {
    this.mounted = false
  }

  setChildPath = () => {
    const { childPath } = this.state
    if ( typeof childPath === 'string' && childPath.length > 1 ) {
      this.setState( { hasChildPath : true } )
    }
  }

  close = () => {
    const { onCancel } = this.props
    this.setState( { blueprintId : null, hasChildPath : false } )
    onCancel()
  }

  createBlueprintView() {
    const { blueprints } = this.state
    const mtypes = ( blueprints && blueprints.map( ( item, index ) => {
      return ( 
        <ListGroupItem
          key={item.id}  
          onClick={( e ) => { this.onItemSelected( item ); }}>
          {item.name}
        </ListGroupItem>
      ) 
    } ) ) || []
    return ( 
      <Flex variant="vSpace" direction="column" align="stretch" className={style.content}>
        <h3>Blueprints</h3>
        <Flex variant="hSpace" className={style.blueprintView} align="stretch">
          <Flex.Layout grow={1}>

            <ListGroup className={style.blueprintList}>
              {mtypes}
            </ListGroup>
          </Flex.Layout>
          <Flex.Layout>
            <div
              className={classnames( style.addTypeButton, style.dynamic )} 
              onClick={e => this.setState( { blueprintId : 'dynamic', targetName : 'Dynamic' } )}>
              <h3>Dynamic</h3>
              <p>
                The types of objects being related might vary. An example might be
                a Smart Home where multiple devices (light bulbs, temperature sensors, 
                locks, etc.) exist within a home.
              </p>
            </div>
          </Flex.Layout>
            
        </Flex>
      </Flex>
    )
  }

  onChildPathChange = ( { data, value } = {} ) => {
    this.setState( { [data] : value } )
  }

  createChildPathView() {
    const { childPath } = this.state
    return ( 
      <Flex variant="vSpace" direction="column" align="stretch" className={style.content}>
        <h4>What is the parent called in the child blueprint?</h4>
        <Flex variant="hSpace" className={style.childPathView}>
          <TextInput value={childPath} onChange={this.onChildPathChange} eventData="childPath" />
        </Flex>
      </Flex>
    )
  }

  createRelationshipView() {
    const { targetName } = this.state
    const { srcName } = this.props
    return ( 
      <div className={style.content}>
        <Flex align="start" wrap="wrap" className={style.relationshipView}>
          <Flex.Layout grow={1}>
            <div className={style.addTypeButton} onClick={() => { this.onCreate( 'oneToOne' ) }}>
              <span>
                <i className="fa fa-fw fa-square-o" />
                <i className="fa fa-fw fa-arrow-right" />
                <i className="fa fa-fw fa-square-o" />
              </span>
              <h3>One to One</h3>
              <p>
                This attribute will reference one 
                {' '}
                <span className={style.targetName}>{targetName}</span>
                {' '}
                instance. 
                That instance can be referenced
                by
                {' '}
                <b>only one</b> 
                {' '}
                <span className={style.sourceName}>{srcName}</span>
                .
              </p>
            </div>
          </Flex.Layout>
          <Flex.Layout grow={1}>
            <div className={style.addTypeButton} onClick={() => { this.onCreate( 'manyToOne' ) }}>
              <span>
                <i className="fa fa-fw fa-clone" />
                <i className="fa fa-fw fa-arrow-right" />
                <i className="fa fa-fw fa-square-o" />
              </span>
              <h3>Many to One</h3>
              <p>
                This attribute will reference one 
                {' '}
                <span className={style.targetName}>{targetName}</span>
                {' '}
                instance. 
                That instance can be referenced by many
                {' '}
                <span className={style.sourceName}>{srcName}</span>
                s.
              </p>
            </div>
          </Flex.Layout>
          <Flex.Layout grow={1}>
            <div className={style.addTypeButton} onClick={() => { this.onCreate( 'oneToMany' ) }}>
              <span>
                <i className="fa fa-fw fa-square-o" />
                <i className="fa fa-fw fa-arrow-right" />
                <i className="fa fa-fw fa-clone" />
              </span>
              <h3>One To Many</h3>
              <p>
                This attribute will reference many 
                {' '}
                <span className={style.targetName}>{targetName}</span>
                {' '}
                instances. 
                These instances can be referenced
                by
                {' '}
                <b>only one</b> 
                {' '}
                <span className={style.sourceName}>{srcName}</span>
                .
              </p>
            </div>
          </Flex.Layout>
          <Flex.Layout grow={1}>
            <div className={style.addTypeButton} onClick={() => { this.onCreate( 'manyToMany' ) }}>
              <span>
                <i className="fa fa-fw fa-clone" />
                <i className="fa fa-fw fa-arrow-right" />
                <i className="fa fa-fw fa-clone" />
              </span>

              <h3>Many To Many</h3>
              <p>
                This attribute will reference many 
                {' '}
                <span className={style.targetName}>{targetName}</span>
                {' '}
                instances. 
                These instances can be referenced
                by many
                {' '}
                <span className={style.sourceName}>{srcName}</span>
                s.
              </p>
            </div>
          </Flex.Layout>
        </Flex>
      </div>
    )
  }

  render() {
    const { blueprintId, loading, hasChildPath } = this.state
    const { show } = this.props

    const showCancel = true
    let view,
      okay = 'Next',
      onOkay,
      cancel = 'Back',
      onCancel,
      title,
      showOkay = true

    if ( blueprintId == null ) {
      view = this.createBlueprintView()
      title = 'What kind of instances will be related?'
      cancel = 'Cancel'
      onCancel = this.close
      showOkay = false
    } else if ( !hasChildPath ) {
      // title = 'What will be the path of the corresponding attribute in the target blueprint?'
      title = 'Parent Path'
      view = this.createChildPathView()
      onOkay = this.setChildPath
      onCancel = () => { this.setState( { blueprintId : null } ) }
    } else {
      okay = null
      onOkay = null
      view = this.createRelationshipView()
      title = 'What kind of Relationship?'
      onCancel = () => { this.setState( { hasChildPath : false } ) }
      showOkay = false
    }
    
    return ( 
      <Dialog
        loading={loading}
        show={show}
        bsSize="large"
        okay={okay}
        no={cancel}
        title={title}
        onCancel={onCancel}
        onOkay={onOkay}>
        {view}
        <Flex className={style.buttons} variant="rowM" justify="flex-end">
          {showCancel ? <Button onClick={onCancel}>{cancel}</Button> : null}
          {showOkay ? <Button onClick={onOkay}>{okay}</Button> : null}
        </Flex>
      </Dialog>
    )
  }

}
